import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { activeInActiveArr, typeOptionsArray } from "../../../../utilities/const";
import {
	ADD_CONTENT,
	ADD_NOTIFICATION,
	CONTENT_UPDATE,
	CONTENT_VIEW,
	NOTIFICATION_VIEW,
	UPDATE_NOTIFICATION,
} from "../../../../services/ApiCalls";
import {
	catchAsync,
	checkResponse,
	removeUnderScoreAndCapitalizeFirstLetter,
} from "../../../../utilities/utilities";
import Loader from "../../../../components/Common/Loader";

const schema = z.object({
	title: z
		.string({ required_error: "Title is required" })
		.min(1, { message: "Title is required" }),
	subject: z
		.string({ required_error: "Subject is required" })
		.min(1, { message: "Subject is required" }),
	body: z
		.string({ required_error: "Body  is required" })
		.min(1, { message: "Body is required" }),
	type: z.string(),
	status: z.string(),
});

const AddNotification = () => {
	const [loader, setLoader] = useState(false);
	const { id } = useParams();
	const [notificationData, setNotificationData] = useState();
	const navigate = useNavigate();
	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(schema),
		defaultValues: {
			status: "active",
		},
		reValidateMode: "onChange",
		values: {
			title: notificationData?.title || "",
			subject: notificationData?.subject || "",
			body: notificationData?.body || "",
			status: notificationData?.status || "active",
			type: notificationData?.type
		},
	});

	const getData = catchAsync(async () => {
		setLoader(true);
		const res = await NOTIFICATION_VIEW(id);
		checkResponse({ res, setData: setNotificationData, setLoader });
	}, setLoader);

	useEffect(() => {
		if (id) getData();
	}, [id]);

	const submitHandler = catchAsync(async (data) => {
		const body = {
			...data,
			...(id && { _id: id }),
		};
		const res = await (id ? UPDATE_NOTIFICATION(body) : ADD_NOTIFICATION(body));
		const success = checkResponse({ res, showSuccess: true });
		if (success) {
			navigate("/notification");
		}
	});

	return (
		<>
			{loader && <Loader />}
			<section className="AddContentPage py-3 position-relative">
				<Container>
					<Row className="justify-content-center">
						<Col lg="12">
							<div className="d-flex align-items-center gap-10">
								<Link
									to="/notification"
									className="border d-flex align-items-center p-2 rounded"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="12"
										height="12"
										viewBox="0 0 10 16"
										fill="none"
									>
										<path
											d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
											fill="#1E232C"
											stroke="#1E232C"
											stroke-width="0.2"
										/>
									</svg>
								</Link>
								<h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
									{id?"Edit":"Add"} Notification
								</h4>
							</div>
						</Col>
						<Col lg="9" className="my-2">
							<Form onSubmit={handleSubmit(submitHandler)}>
								<div className="formWrpper px-sm-4 box p-3 pb-5">
									<Row>
										<Col md="12" className="my-2">
											<label
												htmlFor=""
												className="form-label m-0 fw-sbold text-muted ps-2"
											>
												Title
											</label>
											<input
												type="text"
												className="form-control bg-white"
												name="title"
												{...register("title")}
											/>
											{errors?.title && (
												<p className="text-danger m-0">
													{errors?.title?.message}
												</p>
											)}
										</Col>
										<Col md="12" className="my-2">
											<label
												htmlFor=""
												className="form-label m-0 fw-sbold text-muted ps-2"
											>
												Subject
											</label>
											<input
												type="text"
												className="form-control bg-white"
												name="subject"
												{...register("subject")}
											/>
											{errors?.subject && (
												<p className="text-danger m-0">
													{errors?.subject?.message}
												</p>
											)}
										</Col>
										<Col md="12" className="my-2">
											<label
												htmlFor=""
												className="form-label m-0 fw-sbold text-muted ps-2"
											>
												Body
											</label>

											<CKEditor
												editor={ClassicEditor}
												data={getValues("body")}
												onChange={(event, editor) => {
													const data = editor.getData();
													setValue("body", data);
												}}
											/>

											{errors?.body && (
												<p className="text-danger m-0">
													{errors?.body?.message}
												</p>
											)}
										</Col>
										<Col md="12" className="my-2">
											<label
												htmlFor=""
												className="form-label m-0 fw-sbold text-muted ps-2"
											>
												User Type
											</label>
											<select
												
												className="form-control bg-white"
												name="type"
												{...register("type")}
											>
												{typeOptionsArray.map((item) => {
													return(
														<option value={item.value}>{item.label}</option>
													)
												})}
											</select>
											{errors?.type && (
												<p className="text-danger m-0">
													{errors?.type?.message}
												</p>
											)}
										</Col>
										<Col lg="12" className="my-2">
											<div className="py-2">
												<label
													htmlFor=""
													className="form-label fw-sbold text-muted ps-2 m-0"
												>
													Status
												</label>
												<ul className="list-unstyled ps-0 mb-0 d-flex align-items-start gap-10  ps-2 flex-wrap">
													{activeInActiveArr.map((item) => {
														return (
															<li className="d-flex align-items-center">
																<input
																	type="radio"
																	id={item}
																	name="status"
																	value={item}
																	className="form-check"
																	{...register("status")}
																/>
																<label
																	htmlFor={item}
																	className="form-label fw-sbold text-muted ps-2 m-0"
																>
																	{removeUnderScoreAndCapitalizeFirstLetter(
																		item
																	)}
																</label>
															</li>
														);
													})}
												</ul>
												{errors?.status && (
													<p className="text-danger m-0">
														{errors?.status?.message}
													</p>
												)}
											</div>
										</Col>
										<Col lg="12" className="my-2">
											<div className="btnWrpper mt-3">
												<Button
													className="d-inline-flex align-items-center justify-content-center commonBtn px-5"
													type="submit"
												>
													Save
												</Button>
											</div>
										</Col>
									</Row>
								</div>
							</Form>
						</Col>
					</Row>
				</Container>
			</section>
		</>
	);
};

export default AddNotification;
